import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from 'app/AppContext';
import SelectionStatus from 'portfolio/components/SelectionStatus';
import { useSelectAlgorithmParametersRevisionIndex } from 'widgets/store/shared/algoParameters/selectors';
import {
    useDispatchDecrementAlgorithmParametersRevisionIndex,
    useDispatchIncrementAlgorithmParametersRevisionIndex,
    useDispatchSetAlgorithmParametersRevisionIndex,
} from 'widgets/store/shared/algoParameters/dispatcher';
import { useSelectStrategyParametersStreamDataState } from './store/stream/selectors';
import StrategyParametersGrid from './grid/StrategyParametersGrid';
import Navigation from './navigation/Navigation';
import { computeGridData } from './grid/gridData';
import styleConstants from 'styles/variables.module.scss';
import './StrategyParameters.scss';
import WidgetToolbar from 'widgets/WidgetToolbar/WidgetToolbar';
import { useWidgetMessages } from 'widgets/store/stream/selectors';
import { useDispatchUpdateWidgetComponentState } from 'widgets/store/component/dispatcher';
import { StrategyParametersComponentState } from './store/component/StrategyParametersComponentState';
import { useSelectQuickSearchText } from './store/component/selectors';
import QuickSearch from 'utils/components/QuickSearch/QuickSearch';
import { Button } from '@instinet-analytics/semantic-ui-components';
import buildQuickSearchFunction from 'utils/components/QuickSearch/quickSearchFunction';
import { strategyParamsColumnDefinitions } from 'widgets/strategyParams/grid/strategyParametersColumns';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { useWidgetPortfolioSelectionStatus } from 'widgets/hooks';
import WidgetToolbarShowHidePanel from 'widgets/WidgetToolbarShowHidePanel/WidgetToolbarShowHidePanel';
import { useStrategyParametersConfigSelector } from 'widgets/strategyParams/store/configuration/selectors';
import { useWidgetContext } from 'widgets/WidgetContext';
import { GridApi } from '@instinet-analytics/semantic-ui-components';
import { GridExportButton } from '@instinet-analytics/semantic-ui-components';

const padding = 2 * parseInt(styleConstants.spaceAround);
const toolbarHeight = parseInt(styleConstants.panelHeaderHeight);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);

export default function StrategyParameters() {
    const { widgetKey, height } = useWidgetContext();

    useWidgetStream();

    const { hideToolbar } = useStrategyParametersConfigSelector();

    const streamData = useSelectStrategyParametersStreamDataState();
    const selectionType = streamData?.orderSelection;

    const hasMessages = useWidgetMessages().length > 0;
    const displayToolbar = selectionType !== 'multiple' || hasMessages;

    const toolbarConsumedHeight =
        displayToolbar && !hideToolbar ? toolbarHeight : 0;
    const gridHeight = height - padding - borderWidth - toolbarConsumedHeight;

    const appContext = useContext(AppContext);
    const { timeFormat } = appContext;

    const { hasValidSelection, portfolioSelectionRequirements } =
        useWidgetPortfolioSelectionStatus();

    const displayNavigation = hasValidSelection && selectionType === 'single';

    const gridPages = useMemo(
        () => computeGridData(streamData, timeFormat),
        [streamData, timeFormat]
    );

    const endIndex = gridPages.length - 1;

    const revisionIndex = useSelectAlgorithmParametersRevisionIndex();
    const listIndex = revisionIndex !== undefined ? revisionIndex : endIndex;

    const dispatchSetListIndexAction =
        useDispatchSetAlgorithmParametersRevisionIndex();
    const dispatchIncrementListIndexAction =
        useDispatchIncrementAlgorithmParametersRevisionIndex();
    const dispatchDecrementListIndexAction =
        useDispatchDecrementAlgorithmParametersRevisionIndex();

    const quickSearchText = useSelectQuickSearchText();
    const dispatchUpdateComponentState =
        useDispatchUpdateWidgetComponentState<StrategyParametersComponentState>();

    const onSearchChange = (nextQuickSearchText: string) => {
        dispatchUpdateComponentState({ quickSearchText: nextQuickSearchText });
    };

    const onClearQuickSearch = () => {
        dispatchUpdateComponentState({ quickSearchText: '' });
    };

    useEffect(() => {
        dispatchSetListIndexAction(endIndex);
    }, [endIndex, dispatchSetListIndexAction]);

    const [gridApi, setGridApi] = useState<GridApi | undefined>();

    const columnDefinitions = strategyParamsColumnDefinitions;

    const rowData =
        0 <= listIndex && listIndex < gridPages.length
            ? gridPages[listIndex]
            : [];
    let filteredRowData = rowData;

    if (quickSearchText) {
        // Quick search
        const quickSearchFunction = buildQuickSearchFunction(
            columnDefinitions,
            quickSearchText,
            ['name', 'val'],
            appContext
        );

        filteredRowData = rowData.filter(quickSearchFunction);
    }

    return (
        <div className="StrategyParameters" data-widget-id={widgetKey}>
            {displayToolbar && (
                <WidgetToolbarShowHidePanel mustShow={!hasValidSelection}>
                    <div className="panel-header">
                        <div className="toolbar">
                            <SelectionStatus
                                selectionRequirements={
                                    portfolioSelectionRequirements
                                }
                                widgetName="Instinet Algo Parameters Widget"
                            />
                            {displayNavigation && (
                                <Navigation
                                    listIndex={listIndex}
                                    endIndex={endIndex}
                                    dispatchSetListIndexAction={
                                        dispatchSetListIndexAction
                                    }
                                    dispatchIncrementListIndexAction={
                                        dispatchIncrementListIndexAction
                                    }
                                    dispatchDecrementListIndexAction={
                                        dispatchDecrementListIndexAction
                                    }
                                />
                            )}
                            {selectionType && hasValidSelection && (
                                <QuickSearch
                                    filterApplied={false}
                                    filteredRowCount={filteredRowData.length}
                                    searchText={quickSearchText}
                                    onSearchChange={onSearchChange}
                                    placeholder={'Search for a parameter'}
                                />
                            )}
                        </div>
                        <WidgetToolbar
                            preceedingItems={
                                quickSearchText && selectionType ? (
                                    <Button
                                        className="clear-button"
                                        onClick={onClearQuickSearch}
                                    >
                                        Clear
                                    </Button>
                                ) : undefined
                            }
                            fixedItems={
                                <GridExportButton
                                    csvFileName="Instinet Algo Parameters"
                                    gridApi={gridApi}
                                />
                            }
                        />
                    </div>
                </WidgetToolbarShowHidePanel>
            )}
            {hasValidSelection && selectionType && (
                <StrategyParametersGrid
                    height={gridHeight}
                    rowData={filteredRowData}
                    searchText={quickSearchText}
                    onGridApiSet={setGridApi}
                />
            )}
        </div>
    );
}
